import { getSeasonYear } from "../../../../../../helper/helper";
import type { IMatchDetails, IMatchOfficial } from "./types/matchDetails.d";

export type MatchDetailsRow = {
  label: string;
  value: string;
};

/** API sometimes returns the literal strings "null" / "undefined" for missing names. */
const isNullLikeToken = (part: string) => {
  const t = part.trim().toLowerCase();
  return !t || t === "null" || t === "undefined";
};

const cleanValue = (value?: string | null) => {
  if (value == null) return "-";
  const trimmed = value.trim();
  if (!trimmed) return "-";
  const parts = trimmed.split(/\s+/).filter((p) => !isNullLikeToken(p));
  return parts.length ? parts.join(" ") : "-";
};

const formatEnumValue = (value?: string | null) => {
  if (!value) return "-";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

/** User-facing status aligned with the match header (Live / Upcoming) and home list match chips. */
export const getMatchStatusDisplayLabel = (status?: string | null) => {
  if (!status?.trim()) return "-";
  const key = status.trim().toUpperCase().replace(/\s+/g, "_");
  switch (key) {
    case "IN_PROGRESS":
      return "Live";
    case "UPCOMING":
    case "SCHEDULED":
      return "Upcoming";
    case "ENDED":
    case "COMPLETED":
    case "FINISHED":
      return "Completed";
    default:
      return formatEnumValue(status);
  }
};

const formatDateValue = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatTimeValue = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatOfficialName = (official?: IMatchOfficial | string | null) => {
  if (!official) return "-";
  if (typeof official === "string") return cleanValue(official);

  const fullName = [official.playerFirstName, official.playerLastName]
    .filter((part): part is string => !!part?.trim() && !isNullLikeToken(String(part)))
    .join(" ");

  return cleanValue(fullName || official.name);
};

const formatMatchDays = (startDate?: string | null, scheduledDays?: number | null) => {
  if (!startDate) return "-";

  const totalDays = scheduledDays && scheduledDays > 0 ? scheduledDays : 1;
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return "-";

  if (totalDays === 1) {
    return `${formatDateValue(startDate)} (Day 1 of 1)`;
  }

  const end = new Date(start);
  end.setDate(end.getDate() + totalDays - 1);

  return `${formatDateValue(startDate)} - ${formatDateValue(end.toISOString())} (${totalDays} days)`;
};

const formatHoursOfPlay = (startDate?: string | null, matchEndTime?: string | null) => {
  const start = formatTimeValue(startDate);
  const end = formatTimeValue(matchEndTime);

  if (start === "-" && end === "-") return "-";
  if (start !== "-" && end !== "-") return `${start} - ${end}`;
  return start !== "-" ? start : end;
};

export const getMatchVenueText = (matchSummary?: IMatchDetails) => {
  const ground = matchSummary?.groundId ?? matchSummary?.teamOne?.groundId ?? matchSummary?.teamTwo?.groundId;
  const parts = [ground?.groundName, ground?.groundAddress, matchSummary?.city]
    .map((part) => part?.trim())
    .filter((part): part is string => !!part);

  return parts.length ? parts.join(", ") : "-";
};

export const getMatchPersonDisplayName = (person?: IMatchDetails["playerOfTheMatch"]) =>
  formatOfficialName(person);

export const getMatchOfficialDisplayName = (official?: IMatchOfficial | string | null) =>
  formatOfficialName(official);

export const getMatchHoursOfPlay = (matchSummary?: IMatchDetails) =>
  formatHoursOfPlay(matchSummary?.startDate, matchSummary?.matchEndTime);

export const getMatchDaysText = (matchSummary?: IMatchDetails) =>
  formatMatchDays(matchSummary?.startDate, matchSummary?.scheduledDays);

export const getMatchDetailsRows = (matchSummary?: IMatchDetails): MatchDetailsRow[] => [
  { label: "Toss", value: cleanValue(matchSummary?.tossResult) },
  { label: "League", value: cleanValue(matchSummary?.leagueName) },
  { label: "Series", value: cleanValue(matchSummary?.tournament?.tournamentName) },
  { label: "Season", value: getSeasonYear(matchSummary?.startDate) },
  { label: "Player of The Match", value: getMatchPersonDisplayName(matchSummary?.playerOfTheMatch) },
  { label: "Match Type", value: formatEnumValue(matchSummary?.matchType) },
  { label: "Match Status", value: getMatchStatusDisplayLabel(matchSummary?.matchStatus) },
  { label: "Result", value: cleanValue(matchSummary?.matchResult ?? matchSummary?.equation) },
  { label: "Hours of Play (local time)", value: formatHoursOfPlay(matchSummary?.startDate, matchSummary?.matchEndTime) },
  { label: "Match days", value: formatMatchDays(matchSummary?.startDate, matchSummary?.scheduledDays) },
  { label: "Umpire", value: formatOfficialName(matchSummary?.umpire ?? matchSummary?.availableUmpireResponse) },
  { label: "Second Umpire", value: formatOfficialName(matchSummary?.umpireTwo) },
  { label: "Third Umpire", value: formatOfficialName(matchSummary?.thirdUmpire) },
  { label: "Pitch Type", value: cleanValue(matchSummary?.pitchType) },
];
